import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { landing, landingGradient } from './landingTheme';
import { toPersianDigits } from '@/lib/format/persian';

/**
 * The three steps between signing in and seeing a plan.
 *
 * @remarks
 * This section exists because the single most common question about a tool that sits on top of
 * another one is "so where does my work live now". The answer is: exactly where it lives today,
 * and this says so in the first step rather than leaving somebody to discover it.
 */
const STEPS = [
  {
    title: 'وارد شوید',
    body: 'با همان حساب سازمانی که دارید. رمز جدیدی ساخته نمی‌شود و دسترسی‌ها همان است که هست.',
  },
  {
    title: 'پروژه را اضافه کنید',
    body: 'پروژه‌ای که تیم روی آن کار می‌کند را تحت مدیریت بیاورید. کارهای موجود همان‌جا می‌مانند و ظرف چند ثانیه اینجا دیده می‌شوند.',
  },
  {
    title: 'برنامه را بسازید',
    body: 'فاز تعریف کنید، تاریخ و مدت بدهید، مسئول و تخمین ثبت کنید — و گانت و بار کاری خودشان ساخته می‌شوند.',
  },
] as const;

export function LandingHowItWorks() {
  return (
    <Box id="how" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography sx={{ color: landing.violetSoft, fontWeight: 700, fontSize: 14 }}>
            چطور کار می‌کند
          </Typography>

          <Typography
            component="h2"
            sx={{ fontSize: { xs: 27, md: 38 }, fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            سه قدم تا اولین برنامه
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            mb: { xs: 6, md: 8 },
          }}
        >
          {STEPS.map((step, index) => (
            <Stack
              key={step.title}
              spacing={1.5}
              sx={{
                p: 3.5,
                borderRadius: 3,
                border: 1,
                borderColor: landing.border,
                backgroundColor: landing.surface,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 800,
                  fontSize: 16,
                  color: '#FFFFFF',
                  background: landingGradient,
                }}
              >
                {toPersianDigits(index + 1)}
              </Box>

              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{step.title}</Typography>

              <Typography sx={{ color: landing.textMuted, fontSize: 14, lineHeight: 2 }}>
                {step.body}
              </Typography>
            </Stack>
          ))}
        </Box>

        <ClosingCall />
      </Container>
    </Box>
  );
}

/** The last thing on the page, and the only place it asks for anything. */
function ClosingCall() {
  return (
    <Stack
      spacing={2.5}
      sx={{
        alignItems: 'center',
        textAlign: 'center',
        px: { xs: 3, md: 8 },
        py: { xs: 6, md: 8 },
        borderRadius: 4,
        border: 1,
        borderColor: landing.borderBright,
        backgroundColor: 'rgba(13, 20, 43, 0.7)',
        backgroundImage:
          'radial-gradient(620px 240px at 50% 0%, rgba(124, 92, 255, 0.22), transparent 70%)',
      }}
    >
      <Typography component="h2" sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800 }}>
        کار تیم را یک‌جا ببینید
      </Typography>

      <Typography sx={{ color: landing.textMuted, maxWidth: 560, lineHeight: 2 }}>
        رای‌ویرا روی چیزی که همین حالا دارید سوار می‌شود. چیزی منتقل نمی‌شود و چیزی دو بار
        ثبت نمی‌شود.
      </Typography>

      <Button
        component={RouterLink}
        to="/sign-in"
        size="large"
        sx={{
          mt: 1,
          background: landingGradient,
          color: '#FFFFFF',
          fontWeight: 800,
          px: 5,
          py: 1.5,
          borderRadius: 99,
          boxShadow: '0 18px 40px -14px rgba(124, 92, 255, 0.8)',
          '&:hover': { background: landingGradient, filter: 'brightness(1.08)' },
        }}
      >
        ورود به سامانه
      </Button>
    </Stack>
  );
}
